<template>
  <div
    class="pagoda-table"
    :class="{
      'has-fixed-bottom': hasFixedBottom,
      'has-column-filter': columnFilter,
      'has-validate-msg': validateMsg,
      'loading': loading,
      [`scroll-${scrollPosition}`]: true
    }"
  >
    <div>
      <div v-if="gridBtns.length" class="grid-btns-wrapper">
        <pagoda-row-btns
                :visibles="gridBtns"
                :btns="gridBtnsConfig"
        ></pagoda-row-btns>
      </div>
      <div class="pagoda-table-wrapper">
        <el-table
                ref="tableRef"
                style="width: 100%"
                :data="tableData.data"
                :class="{'has-total': hasTotal}"
                :row-class-name="setRowClassName"
                :cell-class-name="setCellClassName"
                @row-click="handleRowClick"
                @cell-dblclick="onCellDBClick"
                @header-dragend="onHeaderDragend"
                @sort-change="onSortChange"
                @selection-change="onSelectChange"
                v-bind="$attrs"
                v-on="$listeners"
                border
        >
          <template v-if="hasSelectionColumn">
            <el-table-column
                    v-if="selectionColumn === true"
                    class-name="selection-column"
                    type="selection"
                    :selectable="selectable"
                    fixed
            >
            </el-table-column>
            <el-table-column
                    v-else-if="selectionColumn === 'single'"
                    class-name="selection-column"
                    width="48"
                    fixed
            >
              <template slot-scope="scope">
                <el-radio
                        :value="singleSelection"
                        :disabled="!selectable(scope.row, scope.$index)"
                        @input="
                    singleSelection = arguments[0],
                    onSelectChange([tableData.data[singleSelection]]),
                    $emit('selection-change', [tableData.data[singleSelection]])
                  "
                        :label="scope.$index"
                ></el-radio>
              </template>
            </el-table-column>
          </template>
          <el-table-column
                  v-if="hasIndexColumn"
                  :width="indexColumn.width || hasFixedBottom ? (hasSelectionColumn ? '40' : '60') : '40'"
                  :label="indexColumn.label"
                  type="index"
                  fixed
          >
          </el-table-column>
          <el-table-column
                  v-if="hasExpand"
                  type="expand"
          >
            <template slot-scope="props">
              <slot name="expand" v-bind="props"></slot>
            </template>
          </el-table-column>
          <table-column
                  v-for="column in tableHead"
                  :filterRes="filterData.filterRes"
                  :key="column.prop"
                  v-bind="column"
                  :fixed="column.fixed || false"
                  :sortable="column.sortable && 'custom'"
                  :tableColumnWidthHistory="tableColumnWidthHistory"
                  :propColumnIndex="propColumnIndex"
                  :renderHeader="hasSelectionColumnHeaderRender"
          >
            <template slot-scope="{scope, prop}">
              <slot :name="prop" v-bind="scope">
                <div
                        class="cell-content"
                        :class="{
                      'can-edit': isCanEdit(scope.$index, prop),
                    }"
                        v-show="!isEditing(`${prop}-${scope.$index}`)"
                        :data-key="`${prop}-${scope.$index}`"
                >
                  {{ getLabel(scope.row[prop], prop) }}
                </div>
                <div
                        v-if="dataMap[prop] === null || !!getEditType(prop)"
                        v-show="isEditing(`${prop}-${scope.$index}`)"
                        :class="{
                      'is-validate-error': errorMsgCollection[scope.$index][prop]
                    }"
                        @keyup.enter.stop.prevent.exact="keyboardHandler(scope.$index, prop, 'enter')"
                        @keyup.up.stop.prevent.exact="keyboardHandler(scope.$index, prop, 'up')"
                        @keyup.left.stop.prevent.exact="keyboardHandler(scope.$index, prop, 'left')"
                        @keyup.right.stop.prevent.exact="keyboardHandler(scope.$index, prop, 'right')"
                        @keyup.down.stop.prevent.exact="keyboardHandler(scope.$index, prop, 'down')"
                        @keyup.tab.stop.prevent.exact="keyboardHandler(scope.$index, prop, 'right')"
                        @keydown.tab.stop.prevent.exact
                        @keydown.alt.83.prevent="handleGridBtnClick(gridBtnsConfig['save'].onClick, null, gridBtnsConfig['save'])"
                        @keydown.alt.67.prevent="cancel"
                >
                  <el-input
                          v-if="getEditType(prop) === 'input' && !getPropConfig(prop).readOnly"
                          v-model="scope.row[prop]"
                          v-on="getPropEvents(prop)"
                          v-bind="getComponentProps(prop, scope.$index)"
                          class="table-edit-input"
                          @input="handleItemInput(prop, scope.row, arguments)"
                          :ref="getRefProp(scope.$index, prop)"
                  >
                  </el-input>
                  <table-edit-item
                          v-else-if="!(!(prop in dataMap) && getPropConfig(prop).readOnly)"
                          :value="scope.row[prop]"
                          :key="`${prop}-${scope.$index}`"
                          @input="
                      scope.row[prop] = arguments[0],
                      handleItemInput(prop, scope.row, arguments)
                      "
                          @data-map-loaded="$emit('data-map-loaded', arguments[0], arguments[1])"
                          v-on="getPropEvents(prop)"
                          :label="getLabel(scope.row[prop], prop)"
                          :config="getPropConfig(prop)"
                          :props="getComponentProps(prop, scope.$index)"
                          :ref="getRefProp(scope.$index, prop)"
                  >
                    <template :slot="$scopedSlots[`${prop}-slot`] || $slots[`${prop}-slot`] ? `${prop}-slot` : null" slot-scope="props">
                      <slot :name="`${prop}-slot`"  v-bind="Object.assign({}, scope, props)"></slot>
                    </template>
                    <template slot-scope="scope" :slot="`${slotKey}__${slotName}`" v-for="{slotKey, slotName} in hasNameSlots">
                      <slot :name="`${slotKey}__${slotName}`" v-bind="scope"></slot>
                    </template>
                  </table-edit-item>
                  <div class="table-validate-tip" v-show="validateMsg && errorMsgCollection[scope.$index][prop]">
                    {{errorMsgCollection[scope.$index][prop]}}
                  </div>
                </div>
              </slot>
            </template>
          </table-column>
          <el-table-column
                  :label="editColumnConfig.label"
                  :width="editColumnConfig.width"
                  :fixed="editColumnConfig.fixed"
                  v-if="hasEditColumn"
          >
            <template slot-scope="scope">
              <template v-if="!editColumnConfig.collapse">
                <el-button
                        v-for="(button, idx) in editColumnBtns[scope.$index]"
                        :key="idx"
                        :type="button.type"
                        v-bind="button.props"
                        @click.native="button.onClick && button.onClick($event, button, editColumnBtns[scope.$index], _self)"
                >
                  <span v-if="button.text">{{button.text}}</span>
                </el-button>
              </template>
              <el-button
                      v-else
                      v-for="(button, idx) in editColumnBtns[scope.$index].slice(0, editColumnBtns[scope.$index].length > editColumnMaxBtns ? editColumnMaxBtns - 1 : editColumnBtns[scope.$index].length)"
                      :key="idx"
                      type="text"
                      v-bind="button.props"
                      @click.native="button.onClick && button.onClick($event, button, editColumnBtns[scope.$index], _self)"
              >
                <span v-if="button.text">{{button.text}}</span>
              </el-button>
              <el-dropdown
                      v-if="editColumnConfig.collapse && editColumnBtns[scope.$index].length > editColumnMaxBtns"
                      placement="bottom"
                      :show-timeout="100"
              >
                <el-button type="text" class="more-btn">更多</el-button>
                <el-dropdown-menu
                        slot="dropdown"
                >
                  <div
                          class="pagoda-table-edit-column-more"
                          v-for="(button, idx) in editColumnBtns[scope.$index].slice(editColumnMaxBtns - 1, editColumnBtns[scope.$index].length)"
                          :key="idx"
                  >
                    <el-button
                            type="text"
                            v-bind="button.props"
                            @click.native="button.onClick && button.onClick($event, button, editColumnBtns[scope.$index], _self)"
                    >
                      <span v-if="button.text">{{button.text}}</span>
                    </el-button>
                  </div>
                </el-dropdown-menu>
              </el-dropdown>
            </template>
          </el-table-column>
        </el-table>
        <table-fixed-bottom
                v-if="hasFixedBottom"
                :has-index-column="hasIndexColumn"
                :has-selection-column="hasSelectionColumn"
                :has-expand="hasExpand"
                :has-edit-column="hasEditColumn"
                :table-data="tableData"
                :table-head="allTableHead"
                :filter-res="filterData.filterRes"
                @scrolling="scrollPosition = arguments[0]"
                ref="tableFixedBottom"
        ></table-fixed-bottom>
        <div
                class="table-loading"
                :class="{
            'table-loading--mask': firstRender
          }"
                v-show="loading"
        >
          <div class="el-loading-spinner">
            <svg viewBox="25 25 50 50" class="circular">
              <circle cx="50" cy="50" r="20" fill="none" class="path"></circle>
            </svg>
          </div>
        </div>
      </div>
      <el-pagination
              background
              v-if="tableData.totalDataNum"
              v-show="tableData.totalDataNum > 0"
              :layout="layout"
              :total="tableData.totalDataNum"
              :page-size="pgSize"
              :page-sizes="pageSizes"
              :current-page="currentPage"
              @size-change="onSizeChange"
              @current-change="onPageChange"
      >
      </el-pagination>
    </div>
  </div>
</template>

<script>
  import rowBtns from '../../button/row-btns'
  import tableColumnFilter from './table-column-filter'
  import TableFixedBottom from './table-fixed-bottom'
  import TableEditItem from './table-edit-item'
  import tableColumn from './table-column'
  import elTable from './el-table'
  import {
    Checkbox
  } from 'element-ui'
  import {
    getFilterResHistory,
    setFilterResHistory,
    getTableColumnWidthHistory,
    setTableColumnWidthHistory,
  } from './utils'

  import hasNameSlots from '../../mixins/hasNameSlots'

  import {
    isObject,
    isArray,
    isEmpty,
    isPromise,
    isNumber,
    deepCopy,
    getEmptyObject,
    getEmptyArray,
    noop,
    bounce,
    methodsProxy
  } from '../../../utils'

  import {
    addClass,
    removeClass
  } from "../../../utils/dom"

  const proxyMethodMap = []

  const elBtnDefaultProps = {
    size: '',
    loading: false,
    disabled: false
  }

  const editColumnMaxBtns = 3

  const getGridBtnsDefaultConfig = function (vm) {
    return {
      add: {
        text: '新增',
        props: {
          ...elBtnDefaultProps,
          size: vm.gridCustomBtnsSize || vm.size
        },
        onClick: vm.add
      },
      edit: {
        text: '编辑',
        props: {
          ...elBtnDefaultProps,
          size: vm.gridCustomBtnsSize || vm.size
        },
        onClick: vm.bulkEdit
      },
      cancel: {
        text: '撤回',
        props: {
          ...elBtnDefaultProps,
          size: vm.gridCustomBtnsSize || vm.size
        },
        onClick: vm.cancel
      },
      save: {
        text: '保存',
        props: {
          ...elBtnDefaultProps,
          size: vm.gridCustomBtnsSize || vm.size
        },
        onClick: vm.save
      },
      del: {
        text: '删除',
        props: {
          ...elBtnDefaultProps,
          size: vm.gridCustomBtnsSize || vm.size
        },
        onClick: vm.del
      }
    }
  }

  export default {
    name: 'TableOptimize',
    inheritAttrs: false,
    mixins: [hasNameSlots],
    props: {
      layout: {
        type: String,
        default: 'prev, pager, next'
      },
      defaultSort: {
        type: Object,
        default: getEmptyObject
      },
      tableFilterData: {
        type: Object,
        default: getEmptyObject
      },
      request: {
        type: Function,
        default: noop
      },
      format: {
        type: Function
      },
      selectable: {
        type: Function,
        default: () => true
      },
      editColumnConfig: Object,
      addTableEditColumnConfig: Object,
      gridBtns: {
        type: Array,
        default: getEmptyArray
      },
      gridCustomBtns: {
        type: Object,
        default: getEmptyObject
      },
      pageSizes: {
        type: Array,
        default () {
          return [10, 20, 30]
        }
      },
      pageSize: {
        type: Number,
        default: 10
      },
      size: String,
      gridCustomBtnsSize: String,
      dataMap: {
        type: Object,
        default: getEmptyObject
      },
      columnFilter: {
        type: Boolean,
        default: false
      },
      defaultEditing: {
        type: Boolean,
        default: false
      },
      indexColumn: {
        type: [Boolean, Object],
        default: false
      },
      headSelection: {
        type: Boolean,
        default: false
      },
      selectionColumn: {
        type: [Boolean, String],
        default: false
      },
      remenberColumnWidth: {
        type: Boolean,
        default: false
      },
      validateMsg: {
        type: Boolean,
        default: true
      },
      doubleClickEdit: {
        type: Boolean,
        default: true
      },
      clickRowSelect: {
        type: Boolean,
        default: false
      }
    },
    data () {
      let pgSize = this.pageSize

      const gridBtnsConfig = this.initGridBtnsConfig()

      return {
        currentPage: 1,
        singleSelection: null,
        tableData: {
          head: [],
          data: [],
          totalDataNum: 0
        },
        rowsComponentProps: [],
        pgSize,
        editColumnBtns: [],
        scrollPosition: 'left',
        editingItems: [],
        canEditColumn: [],
        gridBtnsConfig,
        errorMsgCollection: [],
        firstRender: true,
        loading: true,
        filterData: {
          filterList: [],
          filterRes: []
        },
        editingList: [],
        hasTotal: false
      }
    },
    created () {
      this.sortable = this.defaultSort
      this.tableEditDataCache = []
      this.selection = []

      this.requestData(1)
    },
    mounted () {
      methodsProxy(
        this,
        this.$refs.tableRef,
        proxyMethodMap
      )

      this.$bus.$on('dolayout', this.updateFixedTables)
    },
    activated () {
      if (!this.$bus._events.dolayout.includes(this.updateFixedTables)) {
        this.$bus.$on('dolayout', this.updateFixedTables)
      }
    },
    deactivated () {
      this.$bus.$off('dolayout', this.updateFixedTables)
    },
    beforeDestroy () {
      this.$bus.$off('dolayout', this.updateFixedTables)
    },
    methods: {
      handleRowClick (row) {
        if (!this.clickRowSelect) return

        if (this.selectable(row, row.$index)) {
          if (this.selectionColumn === 'single') {
            this.setCurrentRow(row.$index)
          }else {
            this.toggleRowSelection(row.$index)
          }
        }
      },
      toggleRowSelection (rowIndex, selected) {
        this.$refs.tableRef.toggleRowSelection(this.tableData.data[rowIndex], selected)
      },
      handleGridBtnClick (cb, e, btnSelf) {
        if(btnSelf.validate){
          const validateRes = this.validateEditingItems()

          if(validateRes) cb(e, btnSelf, this.selection, this.tableData.data)
        }else{
          cb(e, btnSelf, this.selection, this.tableData.data)
        }
      },
      insertRows (targetIndex, rows = [], type) {
        const _targetIndex = type === 'before' ? targetIndex : targetIndex + 1
        let addTableData = rows ? rows.map(rowData => Object.assign({}, this.newRowData, rowData)) : [{...this.newRowData}]
        let errorMsgCollection = []
        let editingItems = []
        let editColumnBtns = []
        let rowsComponentProps = []
        let rowErrorMsg = {}

        this.editingItems.forEach(key => {
          const [prop, rowIndex] = key.split('-')

          if (rowIndex >= _targetIndex) {
            editingItems.push(`${prop}-${+rowIndex + addTableData.length}`)
          } else {
            editingItems.push(key)
          }
        })

        this.rowsComponentProps.forEach((rowComponentProps, rowIndex) => {
          if (rowIndex >= _targetIndex) {
            rowsComponentProps[rowIndex + addTableData.length] = rowComponentProps
          } else {
            rowsComponentProps[rowIndex] = rowComponentProps
          }
        })

        this.tableData.data.splice(_targetIndex, 0, ...addTableData)
        this.tableData.data.forEach((rowData, rowIndex) => {
          let rowComponentProps = {}
          rowData.$index = rowIndex

          this.allTableHead.forEach(({label, prop, multiple, componentProps}) => {
            rowErrorMsg[prop] = null

            if (rowData.$newRow) {
              if(componentProps) {
                if (typeof componentProps === 'function') {
                  rowComponentProps[prop] = componentProps({index: rowIndex, data: rowData})
                } else {
                  rowComponentProps[prop] = componentProps
                }
              }

              if (
                !this.getPropConfig(prop).readOnly
                &&
                this.getPropConfig(prop).constraint
              ) {
                editingItems.push(`${prop}-${rowIndex}`)
              }
            }
          })

          if (rowData.$newRow) {
            rowsComponentProps[rowIndex] = rowComponentProps
          }

          // init editColumn
          if(this.editColumnConfig && this.editColumnConfig.setBtnGroup){
            const currentRowBtnsConfig = this.initRowBtnsConfig(rowData, rowIndex)

            editColumnBtns.push(currentRowBtnsConfig)
          }
        })

        this.errorMsgCollection.splice(_targetIndex, 0, ...Array.from({length: rows.length}, () => rowErrorMsg))

        this.rowsComponentProps = rowsComponentProps
        this.editingItems = editingItems
        this.editColumnBtns = editColumnBtns
        this.rowNum = this.tableData.data.length
      },
      insertRowsBefore (...arg) {
        this.insertRows(...arg, 'before')
      },
      insertRowsAfter (...arg) {
        this.insertRows(...arg, 'after')
      },
      newRows (rows) {
        let addTableData = rows ? rows.map(rowData => Object.assign({}, this.newRowData, rowData)) : [{...this.newRowData}]

        this.beforeUpdateTableData(this.allTableHead, addTableData, false)

        this.tableData.data = [
          ...this.tableData.data,
          ...addTableData
        ]
        this.rowNum = this.tableData.data.length
      },
      delRows (rowsIndexs = [this.tableData.data.length - 1]) {
        let newTableData = []
        let errorMsgCollection = []
        let editColumnBtns = []
        let editingItems = []
        let rowsComponentProps = []

        this.tableData.data.forEach((rowData, rowIndex) => {
          if(!rowsIndexs.includes(rowIndex)) {
            errorMsgCollection.push(this.errorMsgCollection[rowIndex])

            const newRowIndex = newTableData.push(rowData) - 1

            rowData.$index = newRowIndex
            rowsComponentProps[newRowIndex] = this.rowsComponentProps[rowIndex]

            this.allTableHead.forEach(({prop, componentProps}) => {
              if(this.editingItems.includes(`${prop}-${rowIndex}`)){
                editingItems.push(`${prop}-${rowData.$index}`)
              }
            })

            // init editColumn
            if(this.editColumnConfig && this.editColumnConfig.setBtnGroup){
              const currentRowBtnsConfig = this.initRowBtnsConfig(rowData, newRowIndex)

              editColumnBtns.push(currentRowBtnsConfig)
            }
          }
        })

        this.errorMsgCollection = errorMsgCollection
        this.editingItems = editingItems
        this.editColumnBtns = editColumnBtns
        this.tableData.data = newTableData
        this.rowsComponentProps = rowsComponentProps
        this.rowNum = this.tableData.data.length
      },
      editRows (rowsIndexs = this.tableData.data.map((_, rowIndex) => rowIndex)) {
        // 单行编辑
        if(isArray(rowsIndexs) && rowsIndexs.length === 1){
          this.toggleRowSelection(rowsIndexs[0], true)

          this.$nextTick(() => {
            this.edit(rowsIndexs[0])

            this.$nextTick(() => {
              const currentRowCanEditItems = this.editingItems.filter(editingItem => {
                const [_, _rowIndex] = editingItem.split('-')

                return rowsIndexs[0] === +_rowIndex
              })

              const currentRowFirstCanEditItem = currentRowCanEditItems.sort((a, b) => {
                const [prop1] = a.split('-')
                const [prop2] = b.split('-')

                return this.propColumnIndex[prop1] - this.propColumnIndex[prop2]
              })[0]

              this.$nextTick(() => {
                this.canEditRefFocus(currentRowFirstCanEditItem)
              })
            })
          })
          // 批量编辑
        }else {
          this.tableData.data.forEach((_, rowIndex) => {
            this.toggleRowSelection(rowIndex, rowsIndexs.includes(rowIndex))
          })

          this.$nextTick(this.bulkEdit)
        }
      },
      getPropEvents (prop) {
        let listeners = {}

        for (const [eventName, handler] of Object.entries(this.getPropConfig(prop).on || {})) {
          if (eventName !== 'input') {
            listeners[eventName] = handler
          }
        }

        return listeners
      },
      handleItemInput (prop, rowData, arg) {
        const propEvents = this.getPropConfig(prop).on

        this.validateOwn(prop, rowData.$index, arg[0])

        propEvents && typeof propEvents.input === 'function' && propEvents.input(...arg, rowData, this.rowsComponentProps[rowData.$index])
      },
      initGridBtnsConfig () {
        const vm = this
        let gridBtnsConfig = getGridBtnsDefaultConfig(vm)

        for(const [name, customConfig] of Object.entries(this.gridCustomBtns)){
          if(gridBtnsConfig[name]){
            Object.assign(gridBtnsConfig[name], customConfig)
          }else{
            const props = Object.assign({
              ...elBtnDefaultProps,
              size: this.gridCustomBtnsSize || this.size
            }, customConfig.props)

            gridBtnsConfig[name] = Object.assign({}, customConfig, {props})
          }

          const oldClickHandler = gridBtnsConfig[name].onClick

          gridBtnsConfig[name].onClick = function (btns, scope, $event) {
            vm.handleGridBtnClick(oldClickHandler, $event, gridBtnsConfig[name])
          }
        }

        return gridBtnsConfig
      },
      setCellClassName ({row, column, rowIndex, columnIndex}) {
        return column.property && column.property.replace(/([A-Z])/g,"-$1").toLowerCase()
      },
      setRowClassName ({row, rowIndex}) {
        let classes = []

        for(const prop of Object.keys(row)){
          if(this.validateMsg && this.errorMsgCollection[rowIndex][prop]){
            classes.push('has-validate-error')
            break
          }
        }

        if(this.selection.find(({$index}) => $index === rowIndex)) {
          classes.push('selected')
        }

        return classes.join(' ')
      },
      getTableData () {
        return this.tableData.data
      },
      getSelection () {
        return this.selection
      },
      getLabel (data, prop) {
        const dataMap = this.dataMap[prop]
        const displayLen = this.getPropConfig(prop).displayLen

        if(isObject(data)){
          return data.label
        }else if(this.dataMap[prop] === null && typeof data !== 'boolean' && data){
          return '---'
        }else if(isObject(dataMap)){
          if(this.getPropConfig(prop).multiple){
            return data.map(value => dataMap[value]).join(',')
          }else{
            return dataMap[data]
          }
        }else{
          return displayLen && data.length > displayLen ? `${data.slice(0, displayLen)}...` : data
        }
      },
      hasSelectionColumnHeaderRender (h, { column: { label, property }, $index }) {
        if (!property) return label
        const columnIndex = this.propColumnIndex[property]
        const isColumnCanEdit = this.isColumnCanEdit(property)
        const validate = this.getPropConfig(property).validate
        const required = validate && validate.required

        return h('div',
          {
            class: [
              'table-header-content',
              {
                'is-required': isColumnCanEdit && required
              }
            ]
          },
          [
            label,
            //head selection
            this.headSelection
            &&
            isColumnCanEdit
              ?
              h(
                Checkbox,
                {
                  class: 'column-selection-checkbox',
                  props: {
                    value: !!this.canEditColumn[columnIndex]
                  },
                  nativeOn: {
                    click (e) {
                      e.stopPropagation()
                    }
                  },
                  on: {
                    input: (newVal) => {
                      this.canEditColumn.splice(columnIndex, 1, newVal)

                      // 解决CheckBox 选择的时候scrollLeft变为0 的hack
                      if(this.hasFixedBottom){
                        const {
                          tableFixedBottom,
                          tableRef
                        } = this.$refs

                        const {
                          headerWrapper
                        } = tableRef.$refs

                        headerWrapper.scrollLeft = tableFixedBottom.layout.fixedRowScrollLeft
                      }
                    }
                  }
                }
              ) : null
          ])
      },
      isCanEdit (rowIndex, propName) {
        const propColumnIndex = this.propColumnIndex[propName]
        const readOnly = this.tableData.data[rowIndex][propName] && this.tableData.data[rowIndex][propName]['readOnly']
        const editType = this.getEditType(propName)

        return !readOnly && editType && this.canEditColumn[propColumnIndex]
      },
      keyboardHandler (rowIndex, propName, eventType) {
        const { editingRefs } = this
        const currentRef = this.$refs[`${propName}-${rowIndex}-editing`][0]
        const currentRowIndex = currentRef.$$rowIndex
        const currentColumnIndex = currentRef.$$columnIndex
        const currentTableColumnIndex = currentRef.$$tableColumnIndex
        let nextEditingRef

        switch (eventType){
          case 'up':
            nextEditingRef = this.getUpEditingRef(editingRefs, currentRowIndex, currentColumnIndex, currentTableColumnIndex)
            break
          case 'left':
            nextEditingRef = this.getLeftEditingRef(editingRefs, currentRowIndex, currentColumnIndex, currentTableColumnIndex)
            break
          case 'right':
            nextEditingRef = this.getRightEditingRef(editingRefs, currentRowIndex, currentColumnIndex, currentTableColumnIndex)
            break
          case 'down':
            nextEditingRef = this.getDownEditingRef(editingRefs, currentRowIndex, currentColumnIndex, currentTableColumnIndex)
            break
          case 'enter':
            nextEditingRef = this.getRightEditingRef(editingRefs, currentRowIndex, currentColumnIndex, currentTableColumnIndex)
            break
        }

        if(nextEditingRef) {
          nextEditingRef.focus()

          const nextEditType = this.getEditType(nextEditingRef.$$propName)

          if(nextEditType === 'input') {
            nextEditingRef.$el.querySelector('input').setSelectionRange(9999, 9999)
          }
        }
      },
      getUpEditingRef (editingRefs, currentRowIndex, currentColumnIndex, currentTableColumnIndex) {
        const upRowEditingRefs = editingRefs[currentRowIndex - 1]

        // 判断是否有上一行
        if(upRowEditingRefs){
          const upRef = upRowEditingRefs.find(ref => ref.$$tableColumnIndex === currentTableColumnIndex)

          if(upRef){
            return upRef
          }else{
            return upRowEditingRefs[upRowEditingRefs.length - 1]
          }
        }else{
          return null
        }
      },
      getDownEditingRef (editingRefs, currentRowIndex, currentColumnIndex, currentTableColumnIndex) {
        const downRowEditingRefs = editingRefs[currentRowIndex + 1]

        // 判断是否有下一行
        if(downRowEditingRefs){
          const downRef = downRowEditingRefs.find(ref => ref.$$tableColumnIndex === currentTableColumnIndex)

          if(downRef){
            return downRef
          }else{
            return downRowEditingRefs[0]
          }
        }else{
          return null
        }
      },
      getLeftEditingRef (editingRefs, currentRowIndex, currentColumnIndex, currentTableColumnIndex) {
        const leftRowEditingRefs = editingRefs[currentRowIndex][currentColumnIndex - 1]
        const upRowEditingRefs = editingRefs[currentRowIndex - 1]

        // 判断当前行是否有上一个可编辑区域
        if(leftRowEditingRefs){
          return leftRowEditingRefs
        }else if(upRowEditingRefs){
          return upRowEditingRefs[upRowEditingRefs.length - 1]
        }else {
          return null
        }
      },
      getRightEditingRef (editingRefs, currentRowIndex, currentColumnIndex, currentTableColumnIndex) {
        const rightRowEditingRefs = editingRefs[currentRowIndex][currentColumnIndex + 1]
        const downRowEditingRefs = editingRefs[currentRowIndex + 1]

        // 判断当前行是否有上一个可编辑区域
        if(rightRowEditingRefs){
          return rightRowEditingRefs
        }else if(downRowEditingRefs){
          return downRowEditingRefs[0]
        }else {
          return null
        }
      },
      isColumnCanEdit (prop) {
        return !this.allTableHead[this.propColumnIndex[prop]].readOnly
      },
      isEditing (key) {
        return this.editingItems.includes(key)
      },
      getPropConfig (prop) {
        return this.allTableHead[this.propColumnIndex[prop]]
      },
      getComponentProps (prop, rowIndex) {
        return this.rowsComponentProps[rowIndex][prop]
      },
      getEditType (prop) {
        return this.allTableHead[this.propColumnIndex[prop]].constraint
      },
      getRefProp (rowIndex, prop) {
        const key = `${prop}-${rowIndex}`
        let isEditing = this.isEditing(key)

        return `${key}${isEditing ? '-editing' : ''}`
      },
      getEditingRefs () {
        // result 二维数组
        let result = []
        let resultIdx = 0
        let newRow = false

        for(let currentRow = 0;currentRow < this.rowNum;currentRow++){

          for(let currentColumn = 0;currentColumn < this.columnNum;currentColumn++){

            for(const [refName, ref] of Object.entries(this.$refs)){
              const [prop, rowIndex, state] = refName.split('-')
              const columnIndex = this.propColumnIndex[prop]

              if(
                state === 'editing'
                &&
                currentRow === +rowIndex
                &&
                currentColumn === +columnIndex
                &&
                ref[0]
              ){
                const instance = ref[0]

                if(!result[resultIdx]){
                  newRow = true
                  result[resultIdx] = []
                }

                result[resultIdx].push(instance)

                instance.$$rowIndex = resultIdx
                instance.$$columnIndex = result[resultIdx].length - 1
                instance.$$tableRowIndex = currentRow
                instance.$$tableColumnIndex = columnIndex
                instance.$$propName = prop

              }
            }

          }

          if(newRow){
            resultIdx++
            newRow = false
          }

        }

        return result
      },
      onCellDBClick (row, column, cell) {
        if(!this.doubleClickEdit) return

        const cellContent = cell.querySelector('.cell-content.can-edit')
        const key = cellContent && cellContent.getAttribute('data-key')

        if(key && !this.editingItems.includes(key)) {
          this.editingItems.push(key)

          this.$nextTick(() => {
            const ref = this.$refs[`${key}-editing`][0]

            ref.focus()
          })
        }
      },
      onSelectChange (selection) {
        this.selection = selection
      },
      scrollTo (rowIndex) {
        const {
          tableRef
        } = this.$refs

        const {
          bodyWrapper,
          fixedWrapper,
          rightFixedWrapper
        } = tableRef.$refs

        const rows = Array.from(bodyWrapper.querySelectorAll('.el-table__row'))
        const fixedLeftTableRows = Array.from(fixedWrapper.querySelectorAll('.el-table__row'))
        const fixedRightTableRows = Array.from(rightFixedWrapper.querySelectorAll('.el-table__row'))
        let scrollTop = 0

        for(const [_rowIndex, row] of rows.entries()){
          const activeRow = rows[_rowIndex]
          const activefixedLeftTableRow = fixedLeftTableRows[_rowIndex]
          const activefixedRightTableRow = fixedRightTableRows[_rowIndex]

          if(_rowIndex === rowIndex) {
            addClass(activeRow, 'hover-row')
            addClass(activefixedLeftTableRow, 'hover-row')
            addClass(activefixedRightTableRow, 'hover-row')
          }else {
            removeClass(activeRow, 'hover-row')
            removeClass(activefixedLeftTableRow, 'hover-row')
            removeClass(activefixedRightTableRow, 'hover-row')
          }

          if(_rowIndex < rowIndex) {
            scrollTop += parseInt(window.getComputedStyle(row).height)
          }
        }

        bodyWrapper.scrollTop = scrollTop
      },
      validateOwn: bounce(function (prop, rowIndex, result) {
        this.errorMsgCollection[rowIndex][prop] = this.validate(prop, result, rowIndex)
      }, 150),
      validate (prop, result, rowIndex) {
        const {
          validate: validateOpt,
          label
        } = this.allTableHead[this.propColumnIndex[prop]]
        let required
        let rules
        let errorMsg = ''

        if(!validateOpt) return ''

        if(isObject(validateOpt)){
          required = validateOpt.required
          rules = validateOpt.rules || []
        }else if(isArray(validateOpt)){
          rules = validateOpt
        }

        if(required && isEmpty(result)){
          return validateOpt.msg || `请填写${label}`
        }else if(required === false && isEmpty(result)){
          return ''
        }

        for(const {type, value, msg} of rules){

          if(type === 'number' && (result === '' || !isNumber(+result))){
            return msg || `${label}为数字类型`
          }

          if(type === 'minLength' && result.length < value){
            return msg || `${label}最小长度为${value}`
          }

          if(type === 'maxLength' && result.length > value){
            return msg || `${label}最大长度为${value}`
          }

          if(type === 'regexp' && !value.test(result)){
            return msg || `请填写正确的${label}格式`
          }

          if(type === 'custom' && typeof value === 'function' && !value(this.tableData.data[rowIndex])){
            return msg || `${label}不符合规则`
          }

        }

        return ''
      },
      resetValidateState (rowIndexs) {
        let errorMsgCollection = []

        this.tableData.data.forEach((rowData, rowIndex) => {
          const reset = rowIndexs.includes(rowIndex)

          let currentRowValidateMsgs = reset ? {} : this.errorMsgCollection[rowIndex]

          if (reset) {
            for(const prop of Object.keys(rowData)){
              currentRowValidateMsgs[prop] = null
            }
          }

          errorMsgCollection.push(currentRowValidateMsgs)
        })

        this.errorMsgCollection = errorMsgCollection
      },
      validateEditingItems () {
        let validateRes = true

        this.editingItems.forEach(key => {
          const [prop, rowIndex] = key.split('-')
          const value = this.tableData.data[rowIndex][prop]

          let currentPropErrorMsg = this.errorMsgCollection[rowIndex][prop] = this.validate(prop, value, rowIndex)

          if(currentPropErrorMsg){
            validateRes = false
          }
        })

        return validateRes
      },
      cancel () {
        this.cancelRows()
      },
      cancelRows (rowIndexs = Array.from({length: this.tableData.data.length}, (_, rowIndex) => rowIndex)) {
        if(!this.isTableEditing) return

        this.resetValidateState(rowIndexs)
        this.tableEditDataCache.forEach((cacheRowData, rowIndex) => {
          const { $newRow } = cacheRowData

          if ($newRow) {
            this.tableData.data.splice(rowIndex, 1)
          } else {
            this.tableData.data.splice(rowIndex, 1, rowIndexs.includes(rowIndex) ? cacheRowData : this.tableData.data[rowIndex])
          }
        })

        this.editingItems = rowIndexs.length ? this.editingItems.filter(item => {
          const [_, rowIndex] = item.split('-')

          return !rowIndexs.includes(+rowIndex)
        }) : []
      },
      edit (rowIndex) {
        const checkedboxs = this.$el.querySelectorAll('td.selection-column .el-checkbox.is-checked')
        let editingItems = isNumber(rowIndex) ? [...this.editingItems] : []

        Array.from(checkedboxs, checkedbox => {
          const checkedRow = checkedbox.parentNode.parentNode.parentNode
          const checkedRowColumns = checkedRow.querySelectorAll('[class^="el-table"]:not(.selection-column)')

          Array.from(checkedRowColumns, column => {
            const canEditColumn = column.querySelector('.cell-content.can-edit')

            if(canEditColumn){
              const key = canEditColumn.getAttribute('data-key')

              if(!editingItems.includes(key)) {
                if(isNumber(rowIndex)) {
                  const [_, _rowIndex] = key.split('-')

                  if(rowIndex === +_rowIndex){
                    editingItems.push(key)
                  }
                }else{
                  editingItems.push(key)
                }
              }
            }
          })

        })

        this.editingItems = editingItems
      },
      bulkEdit () {
        this.edit()

        this.$nextTick(() => {
          const firstCanEditKey = this.editingItems[0]

          this.canEditRefFocus(firstCanEditKey)
        })
      },
      canEditRefFocus (key) {
        if(!key) return

        const canEditRef = this.$refs[`${key}-editing`][0]

        canEditRef.focus()
      },
      initTableColumnFilter () {
        // 表格列展示隐藏
        this.tableColumnFilter = tableColumnFilter.newInstance({
          tableId: this.tableId,
          tableInstance: this.$refs.tableRef,
          filterData: this.filterData,
          filterChangeCb: (filterRes) => {
            const tableEl = this.$el.children[0]
            // 标志表格重新渲染 解决因表格内容换行导致表格闪烁
            addClass(tableEl ,'rending')

            this.filterData.filterRes = filterRes
            this.filterResHistory = filterRes
            this.remenberColumnWidth && setFilterResHistory(this.tableId, filterRes)

            this.$nextTick(() => {
              this.tableColumnFilter.update(this)
              this.updateFixedTables()
            })

            setTimeout(() => {
              removeClass(tableEl ,'rending')
            }, 60)
          }
        })
      },
      initFixedBottomRow () {
        const {
          tableFixedBottom,
          tableRef
        } = this.$refs

        setTimeout(() => {
          tableFixedBottom.init(tableRef)
        }, 0)
      },
      onSortChange (sortable) {
        this.sortable = sortable

        this.refreshCurrentPage()
      },
      onHeaderDragend (newWidth, _, {property}) {
        const columnIndex = this.propColumnIndex[property]
        this.tableColumnWidthHistory[columnIndex] = newWidth

        if(this.remenberColumnWidth){
          this.tableColumnWidthHistory[columnIndex] = newWidth
          setTableColumnWidthHistory(this.tableId, this.tableColumnWidthHistory)
        }
        this.$nextTick(this.updateFixedTables)
      },
      updateFixedTables () {
        if(!this.hasFixedBottom) return

        const {
          tableFixedBottom,
          tableRef
        } = this.$refs

        setTimeout(() => {
          tableFixedBottom.updateFixedTables(tableRef)
        }, 60)
      },
      onPageChange (currentPage) {
        if (this.currentPage !== currentPage) {
          this.currentPage = currentPage
          this.editingItems = []
          this.requestData(currentPage)
        }
      },
      onSizeChange (size) {
        this.pgSize = size
        this.editingItems = []
        this.refresh()
      },
      setCurrentRow (rowIndex) {
        this.singleSelection = rowIndex
        this.onSelectChange([this.tableData.data[this.singleSelection]])
      },
      requestData (pgNum) {
        const requestPromise = this.request(this.tableFilterData, pgNum, this.pgSize, this.sortable || {})

        this.loading = true

        requestPromise
          .then(res => {
            const result = this.format ? this.format(res) : res
            this.tableData.head = result.head
            this.tableData.data = result.data

            const filterList = Array.from(this.allTableHead, tableDataItem => tableDataItem.label)

            // 根据table id 去获取缓存数据
            this.tableId = result.tableId
            this.filterResHistory = getFilterResHistory(this.tableId)
            this.tableColumnWidthHistory = this.remenberColumnWidth ? (getTableColumnWidthHistory(this.tableId) || []) : []

            this.filterData.filterRes = this.filterResHistory || filterList
            this.filterData.filterList = filterList

            this.propColumnIndex = {}
            this.newRowData = {
              $newRow: true
            }

            this.rowNum = this.tableData.data.length
            this.columnNum = this.tableData.head.length
            this.canEditColumn = Array.from(this.allTableHead, ({readOnly}) => !readOnly)
            this.allTableHead.forEach(({prop, multiple}, idx) => {
              this.propColumnIndex[prop] = idx
              this.newRowData[prop] = multiple ? [] : ''
            })

            this.beforeUpdateTableData(this.allTableHead, result.data)
            this.tableData = Object.assign({}, this.tableData, result)

            this.loading = false

            this.$nextTick(() => {
              if(this.firstRender){
                this.initDefaultSort()
              }

              if(this.columnFilter && !this.tableColumnFilter) this.initTableColumnFilter()

              if(this.hasFixedBottom && !this.$refs.tableFixedBottom.isInit) this.initFixedBottomRow()

              this.firstRender = false
              this.$emit('table-ready', this)
            })
          })

        return requestPromise
      },
      initDefaultSort () {
        const {
          tableRef
        } = this.$refs

        const {
          tableHeader
        } = tableRef.$refs

        tableHeader.columns.forEach(column => {
          if(column.property === this.defaultSort.prop) {
            column.order = this.defaultSort.order ? this.defaultSort.order : 'ascending'
          }
        })
      },
      beforeUpdateTableData (head, data, reset = true) {
        let errorMsgCollection = reset ? [] : [...this.errorMsgCollection]
        let editColumnBtns = reset ? [] : [...this.editColumnBtns]
        let editingItems = reset ? [] : [...this.editingItems]
        let rowsComponentProps = reset ? [] : [...this.rowsComponentProps]

        const startRowIndex = reset ? 0 : this.tableData.data.length

        data.forEach((rowData, rowIndex) => {
          let currentRowValidateMsgs = {}
          const newRowIndex = startRowIndex + rowIndex

          rowData.$index = newRowIndex
          let rowComponentProps = {}

          head.forEach(({label, prop, multiple, componentProps}) => {
            const key = `${prop}-${rowData.$index}`
            currentRowValidateMsgs[prop] = null

            if(!(prop in rowData)){
              rowData[prop] = multiple ? [] : ''
            }

            if(componentProps) {
              if (typeof componentProps === 'function') {
                rowComponentProps[prop] = componentProps({index: newRowIndex, data: rowData})
              } else {
                rowComponentProps[prop] = componentProps
              }
            }

            if(
              (!reset || this.defaultEditing)
              &&
              this.filterData.filterRes.includes(label)
              &&
              !this.getPropConfig(prop).readOnly
              &&
              this.getPropConfig(prop).constraint
              &&
              !editingItems.includes(key)
            ){
              editingItems.push(key)
            }
          })

          errorMsgCollection.push(currentRowValidateMsgs)
          rowsComponentProps.push(rowComponentProps)

          // init editColumn
          if(this.editColumnConfig && this.editColumnConfig.setBtnGroup){
            const currentRowBtnsConfig = this.initRowBtnsConfig(rowData, newRowIndex)

            editColumnBtns.push(currentRowBtnsConfig)
          }
        })

        this.rowsComponentProps = rowsComponentProps
        this.editingItems = editingItems
        this.errorMsgCollection = errorMsgCollection
        this.editColumnBtns = editColumnBtns
        this.rowNum += data.length
      },
      initRowBtnsConfig (rowData, rowIndex) {
        return this.editColumnConfig.setBtnGroup({data: rowData, index: rowIndex}).map(btn => {
          btn.props = Object.assign({
            ...elBtnDefaultProps,
            size: this.size
          }, btn.props)
          return btn
        })
      },
      refresh () {
        this.currentPage = 1
        return this.refreshCurrentPage()
      },
      refreshCurrentPage () {
        return this.requestData(this.currentPage)
      },
      recursiveTraversalTableHead (data, cb) {
        data.forEach(column => {
          if (column.children && column.children.length) {
            this.recursiveTraversalTableHead(column.children, cb)
          } else {
            cb(column)
          }
        })
      }
    },
    watch: {
      tableFilterData () {
        this.refresh()
      },
      editingItems () {
        if (!this.editingItems.length) return

        this.tableEditDataCache = deepCopy(this.tableData.data)

        setTimeout(() => {
          this.editingRefs = this.getEditingRefs()

          this.updateFixedTables()
        }, 60)
      },
      gridCustomBtns () {
        this.gridBtnsConfig = this.initGridBtnsConfig()
      },
      gridCustomBtnsSize () {
        this.gridBtnsConfig = this.initGridBtnsConfig()
      },
      'editColumnConfig.setBtnGroup' () {
        if (typeof this.editColumnConfig.setBtnGroup !== 'function') return

        let editColumnBtns = []

        this.tableData.data.forEach((rowData, rowIndex) => {
          const currentRowBtnsConfig = this.initRowBtnsConfig(rowData, rowIndex)

          editColumnBtns.push(currentRowBtnsConfig)
        })

        this.editColumnBtns = editColumnBtns

        this.$nextTick(this.updateFixedTables)
      }
    },
    computed: {
      editColumnMaxBtns () {
        return this.editColumnConfig.maxBtns || editColumnMaxBtns
      },
      hasFixedBottom () {
        const {
          fixedBottom
        } = this.tableData

        return isArray(fixedBottom) && !!fixedBottom.length
      },
      hasIndexColumn () {
        return !!this.indexColumn || (!this.hasSelectionColumn && !this.hasExpand && this.hasFixedBottom)
      },
      hasExpand () {
        return !!this.$scopedSlots.expand
      },
      hasSelectionColumn () {
        return this.selectionColumn === true || this.selectionColumn === 'single'
      },
      hasEditColumn () {
        return !!this.editColumnConfig
      },
      hasScopedSlotsProp () {
        return this.tableData.head.filter(({prop}) => this.$scopedSlots[`${prop}-slot`]).map(({prop}) => prop)
      },
      isTableEditing () {
        return this.editingItems.length !== 0
      },
      tableHead () {
        let fixedLeftColumn = []
        let notFixedColumn = []
        let fixedRightColumn = []

        this.tableData.head.forEach((column) => {
          const {
            fixed
          } = column

          if(fixed === true || fixed === 'left'){
            fixedLeftColumn.push(column)
          }else if(fixed === 'right'){
            fixedRightColumn.push(column)
          }else{
            notFixedColumn.push(column)
          }
        })

        this.fixedLeftColumnNum = fixedLeftColumn.length
        this.notFixedColumnNum = notFixedColumn.length
        this.fixedRightColumnNum = fixedRightColumn.length

        return [].concat.call(fixedLeftColumn, notFixedColumn, fixedRightColumn)
      },
      allTableHead () {
        let allTableHead = []

        this.recursiveTraversalTableHead(this.tableHead, column => allTableHead.push(column))

        return allTableHead
      }
    },
    components: {
      TableFixedBottom,
      TableEditItem,
      'pagoda-row-btns': rowBtns,
      tableColumn,
      elTable
    }
  }
</script>
